import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  fetchProgressChatContacts,
  fetchProgressChatMessages,
  sendProgressChatMessage,
} from '../services/api';
import { makeThreadKey, makeThreadVersion, useMessageReadState } from '../utils/messageReadState';
import PrivacyNoticeLink from './PrivacyNoticeLink';

export default function ProgressChatPanel({ roleLabel }: any) {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { versions, markThreadRead } = useMessageReadState(user);
  const userId = user?.user_id ?? user?.id;
  const [contacts, setContacts] = useState<any>([]);
  const [selectedKey, setSelectedKey] = useState<any>('');
  const [messages, setMessages] = useState<any>([]);
  const [loadingContacts, setLoadingContacts] = useState<any>(true);
  const [loadingMessages, setLoadingMessages] = useState<any>(false);
  const [error, setError] = useState<any>('');
  const [draft, setDraft] = useState<any>('');
  const [sending, setSending] = useState<any>(false);

  const selectedContact = useMemo(
    () => contacts.find((item) => makeThreadKey(item) === selectedKey) || null,
    [contacts, selectedKey],
  );

  useEffect(() => {
    let mounted = true;

    async function loadContacts() {
      setLoadingContacts(true);
      setError('');
      try {
        const data = await fetchProgressChatContacts();
        const items = Array.isArray(data.items) ? data.items : [];
        if (!mounted) return;
        setContacts(items);
        if (items.length > 0) {
          const paramParticipantId = searchParams?.get('participant_id');
          const paramStudentId = searchParams?.get('student_id');

          let targetContact = null;
          if (paramParticipantId) {
            targetContact = items.find(
              (item) =>
                String(item.participant_id) === String(paramParticipantId) &&
                (!paramStudentId || String(item.student_id) === String(paramStudentId))
            );
          }

          if (!targetContact) {
            targetContact = items.find((item) => item.latest_sent_at) || items[0];
          }

          const chosenKey = targetContact ? makeThreadKey(targetContact) : makeThreadKey(items[0]);
          setSelectedKey((prev) => prev || chosenKey);
        }
      } catch (err: any) {
        if (!mounted) return;
        setError(err.message || 'Failed to load chat contacts.');
      } finally {
        if (mounted) setLoadingContacts(false);
      }
    }

    loadContacts();
    return () => {
      mounted = false;
    };
  }, [searchParams, userId]);

  useEffect(() => {
    let mounted = true;

    async function loadMessages() {
      if (!selectedContact) {
        setMessages([]);
        return;
      }
      setLoadingMessages(true);
      setError('');
      try {
        const data = await fetchProgressChatMessages(
          selectedContact.participant_id,
          selectedContact.student_id,
        );
        if (!mounted) return;
        if (!Array.isArray(data.items)) {
          throw new Error('Invalid message response. Please reopen the conversation.');
        }
        setMessages(data.items);
        await markThreadRead(selectedContact, data.items);
      } catch (err: any) {
        if (!mounted) return;
        setError(err.message || 'Failed to load messages.');
      } finally {
        if (mounted) setLoadingMessages(false);
      }
    }

    loadMessages();
    return () => {
      mounted = false;
    };
  }, [selectedContact, markThreadRead]);

  async function onSendMessage(event) {
    event.preventDefault();
    if (!selectedContact || !draft.trim() || sending) return;

    setSending(true);
    setError('');
    try {
      const created = await sendProgressChatMessage({
        participantId: selectedContact.participant_id,
        studentId: selectedContact.student_id,
        body: draft.trim(),
      });
      setMessages((prev) => [...prev, created]);
      setDraft('');
    } catch (err: any) {
      setError(err.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="ae-chat-shell">
      <header className="ae-page-hero">
        <h1>Progress Chat</h1>
        <p>
          {roleLabel} Connect keeps teachers, students, and families aligned on student growth,
          goals, and support.
        </p>
      </header>

      <div className="ae-chat-layout">
        <aside className="ae-chat-list ae-card">
          <div className="ae-chat-list-title">Conversations</div>
          {loadingContacts ? <div className="ae-chat-empty">Loading chats...</div> : null}
          {!loadingContacts && contacts.length === 0 ? (
            <div className="ae-chat-empty">No connected contacts were found yet.</div>
          ) : null}

          {!loadingContacts && contacts.length > 0
            ? contacts.map((contact) => {
              const key = makeThreadKey(contact);
              const isActive = key === selectedKey;
              return (
                <button
                  key={key}
                  type="button"
                  className={isActive ? 'ae-chat-thread ae-chat-thread-active' : 'ae-chat-thread'}
                  onClick={() => setSelectedKey(key)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                    <div className="ae-chat-thread-main">{contact.participant_name}</div>
                    {contact.has_incoming && contact.unread_count !== 0 && versions[key] !== makeThreadVersion(contact) && (
                      <span className="ae-chat-badge-new">New</span>
                    )}
                  </div>
                  <div className="ae-chat-thread-sub">
                    {contact.participant_role} • {contact.student_name}
                  </div>
                  {contact.latest_message && (
                    <div className="ae-chat-thread-preview">
                      {contact.latest_message}
                    </div>
                  )}
                  {contact.latest_sent_at && (
                    <div className="ae-chat-thread-time">
                      {new Date(contact.latest_sent_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}{' '}
                      {new Date(contact.latest_sent_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                </button>
              );
            })
            : null}
        </aside>

        <article className="ae-chat-panel ae-card">
          {selectedContact ? (
            <>
              <div className="ae-chat-panel-head">
                <div>
                  <div className="ae-chat-target">{selectedContact.participant_name}</div>
                  <div className="ae-chat-context">
                    {selectedContact.participant_role} chat for {selectedContact.student_name}
                  </div>
                </div>
              </div>

              <div className="ae-chat-messages">
                {loadingMessages ? <div className="ae-chat-empty">Loading messages...</div> : null}
                {!loadingMessages && messages.length === 0 ? (
                  <div className="ae-chat-empty">
                    Start the conversation with an update on progress, goals, or support needs.
                  </div>
                ) : null}

                {!loadingMessages && messages.length > 0
                  ? messages.map((message) => (
                    <div
                      key={message.message_id}
                      className={message.is_mine ? 'ae-chat-bubble ae-chat-bubble-mine' : 'ae-chat-bubble'}
                    >
                      <div className="ae-chat-bubble-name">{message.sender_name}</div>
                      <div>{message.body}</div>
                      <div className="ae-chat-bubble-time">
                        {message.sent_at ? new Date(message.sent_at).toLocaleString() : ''}
                      </div>
                    </div>
                  ))
                  : null}
              </div>

              <form className="ae-chat-compose" onSubmit={onSendMessage}>
                <textarea
                  className="ae-chat-input"
                  rows={3}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="Share a progress update, celebrate growth, or ask for support..."
                />
                <button type="submit" className="ae-btn-primary" disabled={sending || !draft.trim()}>
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
                <div style={{ marginTop: 8 }}><PrivacyNoticeLink /></div>
              </form>
            </>
          ) : (
            <div className="ae-chat-empty">
              Select a conversation to begin.
            </div>
          )}
        </article>
      </div>

      {error ? <div className="ae-chat-error">{error}</div> : null}
    </section>
  );
}
